import type { User } from "firebase/auth";

import type { ReactNode } from "react";
import { createContext, useCallback, useMemo, useState } from "react";

import { useMountEffect } from "../hooks/useMountEffect.js";
import {
  type ClinicClientCompatibleAuthState,
  createSignedOutClinicClientAuthState,
  resolveClinicClientCompatibleAuthState,
} from "../services/auth/clinicClient.js";
import { resolveClinicClientKrCompatibleAuthState } from "../services/auth/clinicClientKr.js";
import { auth } from "../services/firebase.js";

export type AuthAccessSource = "clinic-client" | "clinic-client-kr" | null;

export type ClinicCompatibleAuthState = ClinicClientCompatibleAuthState & {
  accessSource: AuthAccessSource;
};

export type ClinicCompatibleAuthContextValue = ClinicCompatibleAuthState & {
  manualUpdateTrigger: () => Promise<void>;
};

const defaultAuthContextValue: ClinicCompatibleAuthContextValue = {
  ...createSignedOutClinicClientAuthState(),
  accessSource: null,
  loadingAuthState: true,
  manualUpdateTrigger: async () => {},
};

const ClinicCompatibleAuthContext =
  createContext<ClinicCompatibleAuthContextValue>(defaultAuthContextValue);

const updateLoadingAuthState = (
  state: ClinicCompatibleAuthState,
): ClinicCompatibleAuthState =>
  state.loadingAuthState ? state : { ...state, loadingAuthState: true };

export function ClinicCompatibleAuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [authState, setAuthState] = useState<ClinicCompatibleAuthState>({
    ...createSignedOutClinicClientAuthState(),
    accessSource: null,
    loadingAuthState: true,
  });

  const handleSignedOut = useCallback(() => {
    setAuthState({
      ...createSignedOutClinicClientAuthState(),
      accessSource: null,
    });
  }, []);

  const updateAuthState = useCallback(
    (nextState: ClinicCompatibleAuthState) => {
      setAuthState(nextState);
    },
    [],
  );

  const resolveAndUpdateAuthState = useCallback(
    async (user: User, options?: { syncClaims?: boolean }) => {
      let clinicClientState: ClinicClientCompatibleAuthState | null = null;

      try {
        clinicClientState = await resolveClinicClientCompatibleAuthState(
          user,
          options,
        );
      } catch (error) {
        console.error("clinic-client 권한 확인 중 오류 발생:", error);
      }

      if (clinicClientState?.currentTeamId) {
        updateAuthState({
          ...clinicClientState,
          accessSource: "clinic-client",
        });
        return;
      }

      const clinicClientKrState =
        await resolveClinicClientKrCompatibleAuthState(user);

      if (clinicClientKrState) {
        updateAuthState({
          user: clinicClientKrState.user,
          authenticated: true,
          isAdmin: false,
          loadingAuthState: false,
          currentTeamId: undefined,
          currentTeamName: undefined,
          currentTeamType: undefined,
          isTeamOwner: false,
          rdsCompanyId: clinicClientKrState.rdsCompanyId,
          rdsAccountId: clinicClientKrState.rdsAccountId,
          accessSource: "clinic-client-kr",
        });
        return;
      }

      await auth.signOut();
    },
    [updateAuthState],
  );

  const manualUpdateTrigger = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      handleSignedOut();
      return;
    }

    setAuthState(updateLoadingAuthState);

    try {
      await resolveAndUpdateAuthState(currentUser);
    } catch (error) {
      console.error("사용자 인증 컨텍스트 구성 중 오류 발생:", error);
      await auth.signOut();
    }
  }, [handleSignedOut, resolveAndUpdateAuthState]);

  useMountEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      if (!user) {
        handleSignedOut();
        return;
      }

      setAuthState(updateLoadingAuthState);

      try {
        await resolveAndUpdateAuthState(user, { syncClaims: true });
      } catch (error) {
        console.error("사용자 인증 컨텍스트 구성 중 오류 발생:", error);
        await auth.signOut();
      }
    });

    return () => unsubscribe();
  });

  const contextValue = useMemo(
    () => ({
      ...authState,
      manualUpdateTrigger,
    }),
    [authState, manualUpdateTrigger],
  );

  return (
    <ClinicCompatibleAuthContext.Provider value={contextValue}>
      {children}
    </ClinicCompatibleAuthContext.Provider>
  );
}

export default ClinicCompatibleAuthContext;
